const CACHE_VERSION = 1;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_LIMIT = 10;

let memoryCatalog = null;
let loadingPromise = null;

const cacheKey = () => {
  const apiEnv = process.env.REACT_APP_API_ENV || '';
  return `patient_search_catalog_v${CACHE_VERSION}:${window.location.host}:${apiEnv}`;
};

export const normalizePatientSearchText = (value) => {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const toSearchRecord = (patient) => {
  const name = patient?.Nome || patient?.nome || '';
  const searchableName = normalizePatientSearchText(patient?.searchableName || name);

  return {
    pacientId: patient?.pacientId || patient?.patientId || '',
    Nome: name,
    CPF: patient?.CPF || '',
    searchableName,
    updatedAt: patient?.updatedAt || '',
    deleted: Boolean(patient?.deleted),
  };
};

const isUsableRecord = (patient) => patient.pacientId && patient.Nome && !patient.deleted;

const readStoredCatalog = () => {
  try {
    const raw = localStorage.getItem(cacheKey());
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.items)) return null;

    return parsed;
  } catch (error) {
    console.warn('Could not read patient search cache:', error);
    return null;
  }
};

const writeStoredCatalog = (items) => {
  try {
    localStorage.setItem(cacheKey(), JSON.stringify({
      timestamp: Date.now(),
      items,
    }));
  } catch (error) {
    console.warn('Could not write patient search cache:', error);
  }
};

const isFresh = (catalog) => {
  return Boolean(catalog?.items?.length) && catalog?.timestamp && Date.now() - catalog.timestamp < CACHE_TTL_MS;
};

export const clearPatientSearchCache = () => {
  memoryCatalog = null;
  loadingPromise = null;
  localStorage.removeItem(cacheKey());
};

export const getPatientSearchCacheStatus = () => {
  const stored = readStoredCatalog();
  const itemCount = memoryCatalog?.items?.length || stored?.items?.length || 0;
  const timestamp = memoryCatalog?.timestamp || stored?.timestamp || null;

  return {
    key: cacheKey(),
    itemCount,
    loadedInMemory: Boolean(memoryCatalog?.items?.length),
    stored: Boolean(stored?.items?.length),
    loading: Boolean(loadingPromise),
    fresh: Boolean((memoryCatalog && isFresh(memoryCatalog)) || (stored && isFresh(stored))),
    timestamp,
    ageSeconds: timestamp ? Math.round((Date.now() - timestamp) / 1000) : null,
  };
};

export const upsertPatientSearchCache = (patient) => {
  const record = toSearchRecord(patient);
  if (!record.pacientId) return;

  const stored = readStoredCatalog();
  const baseItems = memoryCatalog?.items || stored?.items || [];
  const nextItems = baseItems.filter((item) => item.pacientId !== record.pacientId);

  if (!record.deleted && record.Nome) {
    nextItems.push(record);
  }

  nextItems.sort((a, b) => a.searchableName.localeCompare(b.searchableName));
  memoryCatalog = { timestamp: Date.now(), items: nextItems };
  writeStoredCatalog(nextItems);
};

export const removePatientFromSearchCache = (pacientId) => {
  if (!pacientId) return;

  const stored = readStoredCatalog();
  const baseItems = memoryCatalog?.items || stored?.items || [];
  const nextItems = baseItems.filter((item) => item.pacientId !== pacientId);

  memoryCatalog = { timestamp: Date.now(), items: nextItems };
  writeStoredCatalog(nextItems);
};

export const ensurePatientSearchCatalog = async (fetchAllPatients, options = {}) => {
  const { force = false } = options;

  if (!force && memoryCatalog?.items?.length && isFresh(memoryCatalog)) {
    return memoryCatalog.items;
  }

  const stored = readStoredCatalog();
  if (!force && stored?.items?.length && isFresh(stored)) {
    memoryCatalog = stored;
    return stored.items;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = fetchAllPatients()
    .then((patients) => {
      const items = (Array.isArray(patients) ? patients : [])
        .map(toSearchRecord)
        .filter(isUsableRecord)
        .sort((a, b) => a.searchableName.localeCompare(b.searchableName));

      if (!items.length) {
        const patientCount = Array.isArray(patients) ? patients.length : 'non-array';
        console.warn('Patient search catalog warmup returned no usable patients:', patientCount);
        return memoryCatalog?.items || readStoredCatalog()?.items || [];
      }

      memoryCatalog = { timestamp: Date.now(), items };
      writeStoredCatalog(items);
      return items;
    })
    .finally(() => {
      loadingPromise = null;
    });

  return loadingPromise;
};

export const warmPatientSearchCatalog = (fetchAllPatients) => {
  if (loadingPromise || (memoryCatalog?.items?.length && isFresh(memoryCatalog))) {
    return loadingPromise || Promise.resolve(memoryCatalog.items);
  }

  return ensurePatientSearchCatalog(fetchAllPatients).catch((error) => {
    console.warn('Patient search cache warmup failed:', error);
    return [];
  });
};

const scorePatient = (patient, normalizedTerm, tokens) => {
  let score = 0;

  if (patient.searchableName === normalizedTerm) score -= 100;
  if (patient.searchableName.startsWith(normalizedTerm)) score -= 60;
  if (tokens.every((token) => patient.searchableName.split(' ').some((part) => part.startsWith(token)))) {
    score -= 20;
  }

  score += patient.searchableName.length / 1000;
  return score;
};

const searchItems = (items, normalizedTerm, tokens, limit) => {
  return items
    .filter((patient) => tokens.every((token) => patient.searchableName.includes(token)))
    .map((patient) => ({
      patient,
      score: scorePatient(patient, normalizedTerm, tokens),
    }))
    .sort((a, b) => a.score - b.score || a.patient.searchableName.localeCompare(b.patient.searchableName))
    .slice(0, limit)
    .map(({ patient }) => patient);
};

export const searchCachedPatientCatalog = (term, options = {}) => {
  const normalizedTerm = normalizePatientSearchText(term);
  if (normalizedTerm.length < 3) return { results: [], source: 'cache' };

  const tokens = normalizedTerm.split(' ').filter((token) => token.length >= 2);
  if (!tokens.length) return { results: [], source: 'cache' };

  const stored = memoryCatalog?.items?.length ? memoryCatalog : readStoredCatalog();
  if (!stored?.items?.length || !isFresh(stored)) {
    return { results: [], source: 'miss' };
  }

  memoryCatalog = stored;
  const limit = options.limit || DEFAULT_LIMIT;
  return {
    results: searchItems(stored.items, normalizedTerm, tokens, limit),
    source: 'cache',
  };
};

export const debugSearchPatientCatalog = (term, options = {}) => {
  const startedAt = performance.now();
  const result = searchCachedPatientCatalog(term, options);
  return {
    ...result,
    status: getPatientSearchCacheStatus(),
    elapsedMs: Math.round((performance.now() - startedAt) * 100) / 100,
  };
};

export const searchPatientCatalog = async (term, fetchAllPatients, options = {}) => {
  const cached = searchCachedPatientCatalog(term, options);
  if (cached.source === 'cache') {
    return cached;
  }

  const items = await ensurePatientSearchCatalog(fetchAllPatients, options);
  const normalizedTerm = normalizePatientSearchText(term);
  const tokens = normalizedTerm.split(' ').filter((token) => token.length >= 2);
  const limit = options.limit || DEFAULT_LIMIT;

  return {
    results: searchItems(items, normalizedTerm, tokens, limit),
    source: 'cache',
  };
};
